/**
 * useTokenLivePrice
 *
 * Wrapper over `useLivePrice` with the shape existing components use. Prices
 * come from our price service (see useLivePrice). Futures prices are
 * preferred; a spot price is used only when no futures venue lists the token.
 *
 * The prop value `'dynamic'` (or `undefined`) means "pick the venue
 * automatically"; any other value is the preferred exchange.
 */

import { useMemo } from "react";

import { useLivePrice } from "@/hooks/useLivePrice";
import type { FundingInfo, ServiceExchange } from "@/lib/prices";

/**
 * Supported exchange codes. `'dynamic'` means "pick the first exchange where
 * the token is available" and is resolved client-side.
 */
export type LivePriceExchangeCode =
  | "binance"
  | "bybit"
  | "gate"
  | "kucoin"
  | "asterdex"
  | "hyperliquid"
  | "dynamic";

interface UseTokenLivePriceReturn {
  price: number | null;
  isConnected: boolean;
  isSubscribed: boolean;
  error: string | null;
  lastUpdate: number | null;
  /** Funding of the futures market the price comes from. */
  funding: FundingInfo | null;
  /** The price service venue the price comes from, once chosen. */
  exchange: ServiceExchange | null;
}

function resolveExchange(
  exchange: LivePriceExchangeCode | undefined
): string | undefined {
  if (!exchange || exchange === "dynamic") return undefined;
  return exchange;
}

/**
 * Hook to subscribe to live price updates for a token.
 *
 * @param symbol    - Token symbol (e.g., 'BTC', 'BTCUSDT')
 * @param exchange  - Optional exchange code (defaults to `'dynamic'` which
 *                    auto-picks the first exchange that lists the token)
 * @param enabled   - Whether to enable the subscription (for viewport optimisation)
 */
export const useTokenLivePrice = (
  symbol: string | null | undefined,
  exchange: LivePriceExchangeCode = "dynamic",
  enabled: boolean = true
): UseTokenLivePriceReturn => {
  const resolvedExchange = useMemo(() => resolveExchange(exchange), [exchange]);
  const effectiveSymbol = symbol || "";

  const {
    price: priceUpdate,
    market,
    error,
    isConnecting,
  } = useLivePrice(
    effectiveSymbol,
    resolvedExchange,
    false,
    enabled && !!symbol
  );

  // Mark price where the venue has one, else the last trade price.
  const priceNumber = priceUpdate?.markPrice ?? null;
  const lastUpdate = priceUpdate?.timestamp ?? null;

  const isConnected = priceNumber !== null;

  // True once a market was chosen and its price has started flowing.
  const isSubscribed = Boolean(enabled && symbol && market && !isConnecting);

  return {
    price: priceNumber,
    isConnected,
    isSubscribed,
    error,
    lastUpdate,
    funding: priceUpdate?.funding ?? null,
    exchange: market?.exchange ?? null,
  };
};

export default useTokenLivePrice;
